import type { ReactElement } from 'react';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { AdHocAggregateResult, AdHocTrendResult, AnomalyResult } from '@/shared/analytics/AnalyticsTypes';
import type { UserTrendRow } from './SurfaceTopicViewModel';
import RankedBarsViz from './RankedBarsViz';
import RankedListViz from './RankedListViz';
import MatrixViz from './MatrixViz';
import TimeMatrixViz from './TimeMatrixViz';
import DonutViz from './DonutViz';
import TrendViz from './TrendViz';
import AlertsViz from './AlertsViz';
import UserTrendListViz from './UserTrendListViz';

interface TopicVizProps {
    definition: TopicDefinition;
    primary: AdHocAggregateResult;
    basePath: string;
    matrix?: AdHocAggregateResult | null;
    trend?: AdHocTrendResult | null;
    anomalies?: AnomalyResult | null;
    userTrends?: UserTrendRow[];
}

/** Resolves a topic's viz kind to the matching chart, shared by the L1 topic page and the gallery's inline featured lead. */
export default function TopicViz({ definition, primary, basePath, matrix = null, trend = null, anomalies = null, userTrends = [] }: TopicVizProps): ReactElement {
    if (definition.vizKind === 'matrix' && matrix !== null) {
        return <MatrixViz definition={definition} primary={primary} matrix={matrix} basePath={basePath} showViews />;
    }
    if (definition.vizKind === 'time-matrix' && trend !== null) {
        return <TimeMatrixViz definition={definition} primary={primary} trend={trend} basePath={basePath} />;
    }
    if (definition.vizKind === 'alerts') {
        return <AlertsViz definition={definition} anomalies={anomalies} primary={primary} basePath={basePath} />;
    }
    if (definition.vizKind === 'user-trend-list') {
        return <UserTrendListViz definition={definition} rows={userTrends} basePath={basePath} />;
    }
    if (definition.vizKind === 'ranked-list') {
        return <RankedListViz definition={definition} result={primary} basePath={basePath} />;
    }
    if (definition.vizKind === 'donut') {
        return <DonutViz definition={definition} result={primary} basePath={basePath} />;
    }
    if (definition.vizKind === 'trend' && trend !== null) {
        return <TrendViz definition={definition} trend={trend} />;
    }
    return <RankedBarsViz definition={definition} result={primary} basePath={basePath} />;
}
